import {ChangeEvent, useState} from 'react';
import CommonFilesList from './CommonFilesList';
import {useIntl} from 'react-intl';
import {FileToUpload, MAX_UPLOAD_FILE_SIZE, uploadFile} from './api';
import * as Yup from 'yup';
import {FormikProps, useFormik} from 'formik';
import toast from 'react-hot-toast';
import AddDocumentModal from './AddDocumentModal';
import {useQueryClient} from 'react-query';

const AdminDocumentManager = () => {
    const intl = useIntl();
    const [show, setShow] = useState(false);
    const [resetCategoryInputKey, setCategoryKey] = useState(Math.random() * 255);
    const queryClient = useQueryClient();

    const handleClose = () => setShow(false);
    const handleShow = () => {
        setShow(true);
        formik.resetForm();
    };
    const initialValues: FileToUpload = {
        file: undefined,
        category: undefined,
        langue: undefined,
        fileName: '',
        visible: true,
        start_date: '',
    };
    const InitialFormTouched = {
        file: false,
        categorie: true,
        langue: true,
        fileName: false,
        visible: false,
        start_date: true,
    };

    const validationRules: Yup.SchemaOf<FileToUpload> = Yup.object().shape({
        fileName: Yup.string().required(intl.formatMessage({id: 'FIELD.REQUIRED'})),
        category: Yup.mixed().required(intl.formatMessage({id: 'FIELD.REQUIRED'})),
        langue: Yup.mixed().required(intl.formatMessage({id: 'FIELD.REQUIRED'})),
        visible: Yup.boolean()
            .default(false)
            .required(intl.formatMessage({id: 'FIELD.REQUIRED'})),
        start_date: Yup.string().when('visible', {
            is: true,
            then: (schema) => schema.notRequired(),
            otherwise: (schema) => schema.required(intl.formatMessage({id: 'FIELD.REQUIRED'})),
        }),
        file: Yup.mixed()
            .required(intl.formatMessage({id: 'FIELD.REQUIRED'}))
            .test(
                'fileSize',
                intl.formatMessage({id: 'TOO.BIG'}) + `${MAX_UPLOAD_FILE_SIZE / 1000000}MB`,
                (value) => {
                    return value !== undefined && value.size <= MAX_UPLOAD_FILE_SIZE;
                }
            ),
    });

    const formik: FormikProps<FileToUpload> = useFormik({
        initialValues: initialValues,
        initialTouched: InitialFormTouched,
        validationSchema: validationRules,
        validateOnChange: true,
        onSubmit: async (values: FileToUpload, {resetForm}) => {
            const formData: FormData = new FormData();

            for (let value in values) {
                if (value === 'category') {
                    formData.append(value, values.category?.value as string);
                } else if (value === 'langue') {
                    formData.append(value, values.langue?.value as string);
                } else {
                    // @ts-ignore
                    formData.append(value, values[[value as keyof FileToUpload]]);
                }
            }
            const success = await uploadFile(formData);

            if (success) {
                resetForm();
                toast.success(intl.formatMessage({id: 'ADMIN.DOCUMENTS.FORM.SUCCESS'}));
                handleClose();
                await queryClient.invalidateQueries({queryKey: ['get-common-files']});
                return;
            }
            toast.error(intl.formatMessage({id: 'ADMIN.DOCUMENTS.FORM.FAIL'}));
        },
        onReset: () => {
            setCategoryKey(Math.random() * 255);
        },
    });

    const fileOnChange = async (e: ChangeEvent) => {
        const target = e.target as HTMLInputElement;
        if (!target.files || target.files.length <= 0) {
            return;
        }
        const file: File = (target.files as FileList)[0];
        await formik.setFieldValue('file', file);
        await formik.setFieldValue('fileName', file.name);
        await formik.setTouched({...formik.touched, file: true, fileName: true}, true);
    };

    return (
        <>
            <AddDocumentModal
                formik={formik}
                hide={() => handleClose()}
                show={show}
                fileOnChangeHandler={fileOnChange}
                resetCategoryKey={resetCategoryInputKey}
            />
            <CommonFilesList handleShow={handleShow} />
        </>
    );
};

export default AdminDocumentManager;
